import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import apiService, { MonthlyActualBalance } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { usePortfolio } from '../hooks/usePortfolio';
import { localStorageUtils } from '../utils/localStorage';
import { CloseIcon } from './Icons';
import { useModalAccessibility } from '../hooks/useModalAccessibility';
import { getApiFailureMessage } from '../utils/apiErrorMessage';
import './MonthlyAssetModal.css';

interface MonthlySummary {
  year: number;
  month: number;
  totalIncome: number;
  totalExpense: number;
  balance: number;
  savings: number;
  accumulatedBalance: number;
  cashBalance: number;
  actualBalance?: number;
  difference?: number;
  investmentSeedAddition?: number; // 해당 월 투자시드 증액 (순수 현금에서 차감)
}

interface MonthlyAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  monthlySummaries: MonthlySummary[];
  onSummariesChange: (summaries: MonthlySummary[]) => void;
  initialStartYear?: number;
  initialStartMonth?: number;
  initialEndYear?: number;
  initialEndMonth?: number;
  onPeriodChange?: (startYear: number, startMonth: number, endYear: number, endMonth: number) => void;
}

const MonthlyAssetModal: React.FC<MonthlyAssetModalProps> = ({
  isOpen,
  onClose,
  monthlySummaries,
  onSummariesChange,
  initialStartYear,
  initialStartMonth,
  initialEndYear,
  initialEndMonth,
  onPeriodChange
}) => {
  const { modalRef } = useModalAccessibility(isOpen, onClose);
  const { isAuthenticated } = useAuth();
  const { fetchPortfolioSummary } = usePortfolio();
  const [startYear, setStartYear] = useState<number>(initialStartYear || new Date().getFullYear());
  const [startMonth, setStartMonth] = useState<number>(initialStartMonth || new Date().getMonth() + 1);
  const [endYear, setEndYear] = useState<number>(initialEndYear || new Date().getFullYear());
  const [endMonth, setEndMonth] = useState<number>(initialEndMonth || new Date().getMonth() + 1);
  const [editingMonthBalance, setEditingMonthBalance] = useState<{year: number, month: number} | null>(null);
  const [monthBalanceInput, setMonthBalanceInput] = useState<string>('');
  const [editingMonthSeed, setEditingMonthSeed] = useState<{year: number, month: number} | null>(null);
  const [monthSeedInput, setMonthSeedInput] = useState<string>('');
  const isInitializedRef = useRef(false);
  
  // 최신 상태를 추적하기 위한 ref (초기값을 state와 동기화)
  const initialState = {
    startYear: initialStartYear || new Date().getFullYear(), 
    startMonth: initialStartMonth || new Date().getMonth() + 1, 
    endYear: initialEndYear || new Date().getFullYear(), 
    endMonth: initialEndMonth || new Date().getMonth() + 1 
  };
  const stateRef = useRef(initialState);
  
  // 초기 렌더링 시 stateRef를 state와 동기화
  if (stateRef.current.startYear === undefined || stateRef.current.startYear === null) {
    stateRef.current = {
      startYear: startYear,
      startMonth: startMonth,
      endYear: endYear,
      endMonth: endMonth
    };
  }

  // 상태가 변경될 때마다 ref 업데이트
  useEffect(() => {
    stateRef.current = { startYear, startMonth, endYear, endMonth };
  }, [startYear, startMonth, endYear, endMonth]);

  // 모달이 처음 열릴 때만 초기값으로 설정
  useEffect(() => {
    if (isOpen && !isInitializedRef.current) {
      if (initialStartYear !== undefined) setStartYear(initialStartYear);
      if (initialStartMonth !== undefined) setStartMonth(initialStartMonth);
      if (initialEndYear !== undefined) setEndYear(initialEndYear);
      if (initialEndMonth !== undefined) setEndMonth(initialEndMonth);
      isInitializedRef.current = true;
    } else if (!isOpen && isInitializedRef.current) {
      isInitializedRef.current = false;
      // 모달이 닫힐 때 현재 부모의 상태로 리셋
      if (initialStartYear !== undefined) setStartYear(initialStartYear);
      if (initialStartMonth !== undefined) setStartMonth(initialStartMonth);
      if (initialEndYear !== undefined) setEndYear(initialEndYear);
      if (initialEndMonth !== undefined) setEndMonth(initialEndMonth);
    }
    // isOpen만 의존성으로 사용하여 모달이 열려 있는 동안 부모의 상태 변경을 무시
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadMonthlyBalances = async () => {
    try {
      const yearsToLoad: number[] = [];
      for (let year = startYear; year <= endYear; year++) {
        yearsToLoad.push(year);
      }
      
      const allBalances: MonthlyActualBalance[] = [];
      for (const year of yearsToLoad) {
        if (isAuthenticated) {
          const response = await apiService.getMonthlyActualBalancesByYear(year);
          if (response.success && response.data) {
            allBalances.push(...response.data);
          }
        } else {
          const localBalances = localStorageUtils.getMonthlyBalances();
          allBalances.push(...localBalances.filter(b => b.year === year));
        }
      }
      
      // 월별 실제 금액을 monthlySummaries에 병합
      // 실제 금액이 있는 경우 차이 계산 및 누적 잔액 조정
      
      // 실제 금액이 있는 월들을 날짜순으로 정렬
      const actualBalanceMonths = monthlySummaries
        .map(summary => {
          const balance = allBalances.find(b => b.year === summary.year && b.month === summary.month);
          if (balance?.actualBalance !== undefined) {
            return { 
              summary, 
              actualBalance: balance.actualBalance
            };
          }
          return null;
        })
        .filter(item => item !== null)
        .sort((a, b) => {
          const dateA = new Date(a!.summary.year, a!.summary.month - 1, 1);
          const dateB = new Date(b!.summary.year, b!.summary.month - 1, 1);
          return dateA.getTime() - dateB.getTime();
        });
      
      // 실제 금액을 기준으로 순수 현금과 누적 잔액 재계산
      // 기본 계산된 누적잔액에 차이를 누적해서 더함
      let previousActualCashBalance: number | null = null;
      let totalDifferenceAdjustment = 0; // 차이 누적
      
      const updatedSummaries: MonthlySummary[] = [];
      
      for (let i = 0; i < monthlySummaries.length; i++) {
        const summary = monthlySummaries[i];
        const balance = allBalances.find(b => b.year === summary.year && b.month === summary.month);
        
        // 이전 월의 실제 금액 찾기
        let prevActualMonth = null;
        for (let j = i - 1; j >= 0; j--) {
          const prevBalance = allBalances.find(b => 
            b.year === monthlySummaries[j].year && b.month === monthlySummaries[j].month
          );
          if (prevBalance?.actualBalance !== undefined) {
            prevActualMonth = {
              year: monthlySummaries[j].year,
              month: monthlySummaries[j].month,
              actualBalance: prevBalance.actualBalance
            };
            break;
          }
        }
        
        // 현재 월의 실제 금액이 있는 경우
        if (balance?.actualBalance !== undefined) {
          // 순수 현금 계산 (이전 월 실제 금액 기준)
          const cashBalanceBasedOnPrevActual = prevActualMonth 
            ? prevActualMonth.actualBalance + summary.balance
            : summary.cashBalance;
          
          // 차이 = 실제 금액 - 순수 현금
          const difference = balance.actualBalance - cashBalanceBasedOnPrevActual;
          
          // 차이 누적
          totalDifferenceAdjustment += difference;
          
          // 누적잔액 = 기본 계산된 누적잔액 + 차이 누적
          const newAccumulatedBalance = summary.accumulatedBalance + totalDifferenceAdjustment;
          
          // 다음 달을 위한 순수 현금 기준점 업데이트
          previousActualCashBalance = balance.actualBalance;
          
          updatedSummaries.push({
            ...summary,
            actualBalance: balance.actualBalance,
            difference,
            cashBalance: prevActualMonth ? cashBalanceBasedOnPrevActual : summary.cashBalance,
            accumulatedBalance: newAccumulatedBalance,
            investmentSeedAddition: balance?.investmentSeedAddition
          });
        } else {
          // 현재 월에 실제 금액이 없는 경우: 전월 순수현금 + 당월 잔액으로 표시
          const cashBalanceBasedOnPrevActual: number = previousActualCashBalance !== null
            ? previousActualCashBalance + summary.balance
            : summary.cashBalance;
          
          // 누적잔액 = 기본 계산된 누적잔액 + 차이 누적
          const newAccumulatedBalance = summary.accumulatedBalance + totalDifferenceAdjustment;
          
          // 다음 달을 위해 기준점 갱신 (실제금액이 없어도 계산된 순수현금으로 이어감)
          previousActualCashBalance = cashBalanceBasedOnPrevActual;
          
          updatedSummaries.push({
            ...summary,
            actualBalance: balance?.actualBalance,
            difference: balance?.difference,
            cashBalance: cashBalanceBasedOnPrevActual,
            accumulatedBalance: newAccumulatedBalance,
            investmentSeedAddition: balance?.investmentSeedAddition
          });
        }
      }
      
      onSummariesChange(updatedSummaries);
    } catch (error) {
      console.error('월별 실제 금액 로드 실패:', error);
    }
  };


  // 모달이 열릴 때는 loadMonthlyBalances를 호출하지 않음
  // AssetManagementPage에서 이미 계산된 monthlySummaries를 받아서 사용
  // 실제 금액 저장/삭제 시에만 계산이 필요하면 해당 함수 내에서 처리
  // useEffect(() => {
  //   if (isOpen) {
  //     loadMonthlyBalances();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isOpen, startYear, endYear]);

  const deleteMonthlyBalance = async (year: number, month: number) => {
    try {
      const summary = monthlySummaries.find(s => s.year === year && s.month === month);
      if (!summary) {
        alert('해당 월의 데이터를 찾을 수 없습니다.');
        return;
      }

      // 이전에 저장된 실제 금액이 있었는지 확인
      const previousActualBalance = summary.actualBalance;
      if (previousActualBalance === undefined) {
        alert('삭제할 실제 금액이 없습니다.');
        return;
      }

      // 이전 실제 금액과 원래 계산된 순수 현금의 차이
      // difference = actualBalance - cashBalance이므로
      // previousAdjustment = previousActualBalance - summary.cashBalance
      const previousAdjustment = summary.difference !== undefined 
        ? summary.difference 
        : previousActualBalance - summary.cashBalance;

      if (isAuthenticated) {
        // 백엔드에서 삭제
        const response = await apiService.deleteMonthlyActualBalance(year, month);
        if (response.success) {
          // 해당 월과 이후 월들의 누적 잔액 조정 (이전 조정량 제거)
          const updatedSummaries = monthlySummaries.map(s => {
            const summaryDate = new Date(s.year, s.month - 1, 1);
            const targetDate = new Date(year, month - 1, 1);
            
            // 해당 월: actualBalance 제거, accumulatedBalance 조정 (순수 현금은 원래 값 유지)
            if (s.year === year && s.month === month) {
              return {
                ...s,
                actualBalance: undefined,
                difference: undefined,
                cashBalance: s.cashBalance, // 순수 현금은 원래 계산된 값 유지
                accumulatedBalance: s.accumulatedBalance - previousAdjustment // 이전 조정량 제거
              };
            }
            // 이후 월: accumulatedBalance 조정 (이전 조정량 제거)
            else if (summaryDate > targetDate) {
              return {
                ...s,
                accumulatedBalance: s.accumulatedBalance - previousAdjustment
              };
            }
            return s;
          });
          onSummariesChange(updatedSummaries);
          alert('실제 금액이 삭제되었습니다.');
        }
      } else {
        // 로컬스토리지에서 삭제
        const localBalances = localStorageUtils.getMonthlyBalances();
        const existingIndex = localBalances.findIndex(b => b.year === year && b.month === month);
        
        if (existingIndex >= 0) {
          localBalances.splice(existingIndex, 1);
          localStorageUtils.setMonthlyBalances(localBalances);
        }
        
        // 해당 월과 이후 월들의 누적 잔액 조정 (이전 조정량 제거)
        const updatedSummaries = monthlySummaries.map(s => {
          const summaryDate = new Date(s.year, s.month - 1, 1);
          const targetDate = new Date(year, month - 1, 1);
          
          // 해당 월: actualBalance 제거, accumulatedBalance 조정 (순수 현금은 원래 값 유지)
          if (s.year === year && s.month === month) {
            return {
              ...s,
              actualBalance: undefined,
              difference: undefined,
              cashBalance: s.cashBalance, // 순수 현금은 원래 계산된 값 유지
              accumulatedBalance: s.accumulatedBalance - previousAdjustment // 이전 조정량 제거
            };
          }
          // 이후 월: accumulatedBalance 조정 (이전 조정량 제거)
          else if (summaryDate > targetDate) {
            return {
              ...s,
              accumulatedBalance: s.accumulatedBalance - previousAdjustment
            };
          }
          return s;
        });
        onSummariesChange(updatedSummaries);
        alert('실제 금액이 삭제되었습니다.');
      }
    } catch (error: any) {
      console.error('실제 금액 삭제 실패:', error);
      alert(`실제 금액 삭제 실패: ${getApiFailureMessage(error)}`);
    }
  };

  const saveMonthlySeedAddition = async (year: number, month: number, amountOrClear?: number) => {
    try {
      const isClear = amountOrClear === 0;
      const amount = isClear ? 0 : (amountOrClear ?? (monthSeedInput.trim() === '' ? undefined : Number(monthSeedInput)));
      if (!isClear && (amount === undefined || isNaN(amount))) {
        alert('올바른 숫자를 입력해주세요.');
        return;
      }
      if (!isClear && amount != null && amount < 0) {
        alert('0 이상의 값을 입력해주세요.');
        return;
      }

      if (isAuthenticated) {
        const response = await apiService.saveMonthlyInvestmentSeedAddition(year, month, isClear ? 0 : amount);
        if (response.success) {
          const valueToSet = (isClear || amount === 0) ? undefined : amount;
          const updatedSummaries = monthlySummaries.map(s =>
            s.year === year && s.month === month ? { ...s, investmentSeedAddition: valueToSet } : s
          );
          onSummariesChange(updatedSummaries);
          fetchPortfolioSummary();
          setEditingMonthSeed(null);
          setMonthSeedInput('');
          alert(isClear ? '투자시드 증액이 삭제되었습니다.' : '투자시드 증액이 저장되었습니다.');
        }
      } else {
        const valueToSet = (isClear || amount === 0) ? undefined : amount;
        const updatedSummaries = monthlySummaries.map(s =>
          s.year === year && s.month === month ? { ...s, investmentSeedAddition: valueToSet } : s
        );
        onSummariesChange(updatedSummaries);
        setEditingMonthSeed(null);
        setMonthSeedInput('');
        alert(isClear ? '투자시드 증액이 삭제되었습니다.' : '투자시드 증액이 저장되었습니다.');
      }
    } catch (error: any) {
      console.error('투자시드 증액 저장 실패:', error);
      alert(`저장 실패: ${getApiFailureMessage(error)}`);
    }
  };

  const saveMonthlyBalance = async (year: number, month: number) => {
    try {
      // 빈 문자열이면 삭제 처리
      if (!monthBalanceInput || monthBalanceInput.trim() === '') {
        await deleteMonthlyBalance(year, month);
        return;
      }

      const actualBalance = Number(monthBalanceInput);
      if (isNaN(actualBalance)) {
        alert('올바른 숫자를 입력해주세요.');
        return;
      }

      const summary = monthlySummaries.find(s => s.year === year && s.month === month);
      if (!summary) {
        alert('해당 월의 데이터를 찾을 수 없습니다.');
        return;
      }
      
      // 차이 계산: 실제 금액 - 순수 현금
      // 실제 금액은 순수 현금 기준으로 입력되므로, 차이도 순수 현금 기준으로 계산
      const difference = actualBalance - summary.cashBalance;
      
      // 누적 잔액 조정량 계산 (순수 현금 차이 = 누적 잔액 차이)
      // 실제 금액이 순수 현금 기준이므로, 차이를 그대로 누적 잔액에 반영
      const balanceAdjustment = difference;

      if (isAuthenticated) {
        const response = await apiService.saveMonthlyActualBalance(
          year,
          month,
          actualBalance,
          summary.cashBalance // calculatedBalance 대신 cashBalance 전달
        );
        if (response.success) {
          // 실제 금액을 기준으로 순수 현금과 누적 잔액 재계산
          // 이전 월의 실제 금액을 기준으로 다음 달 계산
          // 먼저 모든 실제 금액 로드
          const yearsToLoad: number[] = [];
          for (let y = startYear; y <= endYear; y++) {
            yearsToLoad.push(y);
          }
          
          const allBalancesForRecalc: MonthlyActualBalance[] = [];
          for (const y of yearsToLoad) {
            const resp = await apiService.getMonthlyActualBalancesByYear(y);
            if (resp.success && resp.data) {
              allBalancesForRecalc.push(...resp.data);
            }
          }
          
          // 현재 저장한 실제 금액도 추가/업데이트
          const existingIndex = allBalancesForRecalc.findIndex(b => b.year === year && b.month === month);
          const savedBalance: MonthlyActualBalance = {
            id: existingIndex >= 0 ? allBalancesForRecalc[existingIndex].id : Date.now(),
            year,
            month,
            actualBalance,
            difference: actualBalance - summary.cashBalance
          };
          if (existingIndex >= 0) {
            allBalancesForRecalc[existingIndex] = savedBalance;
          } else {
            allBalancesForRecalc.push(savedBalance);
          }
          
          // 기본 계산된 누적잔액에 차이를 누적해서 더함
          let previousActualCashBalance: number | null = null;
          let totalDifferenceAdjustment = 0;
          
          const updatedSummaries: MonthlySummary[] = [];
          
          for (let i = 0; i < monthlySummaries.length; i++) {
            const s = monthlySummaries[i];
            const sBalance = allBalancesForRecalc.find(b => b.year === s.year && b.month === s.month);
            const isCurrentMonth = s.year === year && s.month === month;
            const sActualBalance = isCurrentMonth ? actualBalance : sBalance?.actualBalance;
            
            // 이전 월의 실제 금액 찾기
            let prevActualMonth = null;
            for (let j = i - 1; j >= 0; j--) {
              const prevBalance = allBalancesForRecalc.find(b => 
                b.year === monthlySummaries[j].year && b.month === monthlySummaries[j].month
              );
              const prevIsCurrentMonth = monthlySummaries[j].year === year && monthlySummaries[j].month === month;
              const prevActualBalance = prevIsCurrentMonth ? actualBalance : prevBalance?.actualBalance;
              if (prevActualBalance !== undefined) {
                prevActualMonth = {
                  year: monthlySummaries[j].year,
                  month: monthlySummaries[j].month,
                  actualBalance: prevActualBalance
                };
                break;
              }
            }
            
            // 현재 월의 실제 금액이 있는 경우
            if (sActualBalance !== undefined) {
              // 순수 현금 계산 (이전 월 실제 금액 기준)
              const cashBalanceBasedOnPrevActual = prevActualMonth 
                ? prevActualMonth.actualBalance + s.balance
                : s.cashBalance;
              
              // 차이 = 실제 금액 - 순수 현금
              const diff = sActualBalance - cashBalanceBasedOnPrevActual;
              
              // 차이 누적
              totalDifferenceAdjustment += diff;
              
              // 누적잔액 = 기본 계산된 누적잔액 + 차이 누적
              const newAccumulatedBalance = s.accumulatedBalance + totalDifferenceAdjustment;
              
              previousActualCashBalance = sActualBalance;
              
              updatedSummaries.push({
                ...s,
                actualBalance: sActualBalance,
                difference: diff,
                cashBalance: prevActualMonth ? cashBalanceBasedOnPrevActual : s.cashBalance,
                accumulatedBalance: newAccumulatedBalance
              });
            } else {
              // 현재 월에 실제 금액이 없는 경우: 전월 순수현금 + 당월 잔액으로 표시
              const cashBalanceBasedOnPrevActual: number = previousActualCashBalance !== null
                ? previousActualCashBalance + s.balance
                : s.cashBalance;
              
              // 누적잔액 = 기본 계산된 누적잔액 + 차이 누적
              const newAccumulatedBalance = s.accumulatedBalance + totalDifferenceAdjustment;
              
              previousActualCashBalance = cashBalanceBasedOnPrevActual;
              
              updatedSummaries.push({
                ...s,
                actualBalance: sBalance?.actualBalance,
                difference: sBalance?.difference,
                cashBalance: cashBalanceBasedOnPrevActual,
                accumulatedBalance: newAccumulatedBalance
              });
            }
          }
          
          onSummariesChange(updatedSummaries);
          setEditingMonthBalance(null);
          setMonthBalanceInput('');
          alert('실제 금액이 저장되었습니다.');
        }
      } else {
        // 로컬스토리지 저장
        const localBalances = localStorageUtils.getMonthlyBalances();
        const existingIndex = localBalances.findIndex(b => b.year === year && b.month === month);
        const newBalance: MonthlyActualBalance = {
          id: existingIndex >= 0 ? localBalances[existingIndex].id : Date.now(),
          year,
          month,
          actualBalance,
          difference: actualBalance - summary.cashBalance
        };
        
        if (existingIndex >= 0) {
          localBalances[existingIndex] = newBalance;
        } else {
          localBalances.push(newBalance);
        }
        
        localStorageUtils.setMonthlyBalances(localBalances);
        
        // 기본 계산된 누적잔액에 차이를 누적해서 더함
        let previousActualCashBalance: number | null = null;
        let totalDifferenceAdjustment = 0;
        
        const updatedSummaries: MonthlySummary[] = [];
        
        for (let i = 0; i < monthlySummaries.length; i++) {
          const s = monthlySummaries[i];
          const sBalance = localBalances.find(b => b.year === s.year && b.month === s.month);
          const isCurrentMonth = s.year === year && s.month === month;
          const sActualBalance = isCurrentMonth ? actualBalance : sBalance?.actualBalance;
          
          // 이전 월의 실제 금액 찾기
          let prevActualMonth = null;
          for (let j = i - 1; j >= 0; j--) {
            const prevBalance = localBalances.find(b => 
              b.year === monthlySummaries[j].year && b.month === monthlySummaries[j].month
            );
            const prevIsCurrentMonth = monthlySummaries[j].year === year && monthlySummaries[j].month === month;
            const prevActualBalance = prevIsCurrentMonth ? actualBalance : prevBalance?.actualBalance;
            if (prevActualBalance !== undefined) {
              prevActualMonth = {
                year: monthlySummaries[j].year,
                month: monthlySummaries[j].month,
                actualBalance: prevActualBalance
              };
              break;
            }
          }
          
          // 현재 월의 실제 금액이 있는 경우
          if (sActualBalance !== undefined) {
            // 순수 현금 계산 (이전 월 실제 금액 기준)
            const cashBalanceBasedOnPrevActual = prevActualMonth 
              ? prevActualMonth.actualBalance + s.balance
              : s.cashBalance;
            
            // 차이 = 실제 금액 - 순수 현금
            const diff = sActualBalance - cashBalanceBasedOnPrevActual;
            
            // 차이 누적
            totalDifferenceAdjustment += diff;
            
            // 누적잔액 = 기본 계산된 누적잔액 + 차이 누적
            const newAccumulatedBalance = s.accumulatedBalance + totalDifferenceAdjustment;
            
            previousActualCashBalance = sActualBalance;
            
            updatedSummaries.push({
              ...s,
              actualBalance: sActualBalance,
              difference: diff,
              cashBalance: prevActualMonth ? cashBalanceBasedOnPrevActual : s.cashBalance,
              accumulatedBalance: newAccumulatedBalance
            });
          } else {
            // 현재 월에 실제 금액이 없는 경우: 전월 순수현금 + 당월 잔액으로 표시
            const cashBalanceBasedOnPrevActual: number = previousActualCashBalance !== null
              ? previousActualCashBalance + s.balance
              : s.cashBalance;
            
            // 누적잔액 = 기본 계산된 누적잔액 + 차이 누적
            const newAccumulatedBalance = s.accumulatedBalance + totalDifferenceAdjustment;
            
            previousActualCashBalance = cashBalanceBasedOnPrevActual;
            
            updatedSummaries.push({
              ...s,
              actualBalance: sBalance?.actualBalance,
              difference: sBalance?.difference,
              cashBalance: cashBalanceBasedOnPrevActual,
              accumulatedBalance: newAccumulatedBalance
            });
          }
        }
        
        onSummariesChange(updatedSummaries);
        setEditingMonthBalance(null);
        setMonthBalanceInput('');
        alert('실제 금액이 저장되었습니다.');
      }
    } catch (error: any) {
      console.error('실제 금액 저장 실패:', error);
      alert(`실제 금액 저장 실패: ${getApiFailureMessage(error)}`);
    }
  };

  // 기간 변경 핸들러 (모든 값을 명시적으로 전달받음)
  const handlePeriodChange = useCallback((newStartYear: number, newStartMonth: number, newEndYear: number, newEndMonth: number) => {
    setStartYear(newStartYear);
    setStartMonth(newStartMonth);
    setEndYear(newEndYear);
    setEndMonth(newEndMonth);
    
    stateRef.current = {
      startYear: newStartYear,
      startMonth: newStartMonth,
      endYear: newEndYear,
      endMonth: newEndMonth
    };
    
    onPeriodChange?.(newStartYear, newStartMonth, newEndYear, newEndMonth);
  }, [onPeriodChange]);

  // 기간 내의 모든 월 요약 (기간 변경 시 자동 재계산)
  const periodSummaries = useMemo(() => {
    return monthlySummaries.filter(
      summary => {
        const summaryDate = new Date(summary.year, summary.month - 1, 1);
        const startDate = new Date(startYear, startMonth - 1, 1);
        // 종료월까지 포함하도록 endMonth + 1월의 0일 사용
        const endDate = new Date(endYear, endMonth + 1, 0);
        const isInRange = summaryDate >= startDate && summaryDate <= endDate;
        return isInRange;
      }
    );
  }, [monthlySummaries, startYear, startMonth, endYear, endMonth]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        ref={modalRef}
        className="monthly-asset-modal modal-animated"
        role="dialog"
        aria-modal="true"
        aria-labelledby="monthly-asset-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="monthly-asset-title">월별 자산 변화</h2>
          <button className="modal-close-btn" onClick={onClose}><CloseIcon size={20} color="currentColor" /></button>
        </div>

        <div className="modal-content">
          <div className="period-selector-section">
            <div className="period-selector-wrapper">
              <div className="period-group-wrapper">
                <label className="period-label-top">시작</label>
                <div className="period-select-group">
                  <select 
                    value={startYear}
                    onChange={(e) => {
                      const newYear = Number(e.target.value);
                      const start = new Date(newYear, startMonth - 1, 1);
                      const end = new Date(endYear, endMonth, 0);
                      if (start > end) {
                        alert('시작 기간이 종료 기간보다 이후일 수 없습니다. 종료 기간을 먼저 변경해주세요.');
                        return;
                      }
                      handlePeriodChange(newYear, startMonth, endYear, endMonth);
                    }}
                    className="period-select"
                  >
                    {Array.from({ length: 201 }, (_, i) => {
                      const currentYear = new Date().getFullYear();
                      const year = currentYear - 100 + i;
                      return (
                        <option key={year} value={year}>{year}년</option>
                      );
                    })}
                  </select>
                  <select 
                    value={startMonth}
                    onChange={(e) => {
                      const newMonth = Number(e.target.value);
                      // 종료기간이 시작기간보다 과거인지 검증
                      const startDate = new Date(startYear, newMonth - 1, 1);
                      const endDate = new Date(endYear, endMonth, 0);
                      if (startDate > endDate) {
                        alert('시작 기간이 종료 기간보다 이후일 수 없습니다. 종료 기간을 먼저 변경해주세요.');
                        return;
                      }
                      // 모든 값을 명시적으로 전달 (클로저 문제 방지)
                      handlePeriodChange(startYear, newMonth, endYear, endMonth);
                    }}
                    className="period-select"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <option key={month} value={month}>{month}월</option>
                    ))}
                  </select>
                </div>
              </div>
              <span className="period-separator">~</span>
              <div className="period-group-wrapper">
                <label className="period-label-top">종료</label>
                <div className="period-select-group">
                  <select 
                    value={endYear}
                    onChange={(e) => {
                      const newYear = Number(e.target.value);
                      const start = new Date(startYear, startMonth - 1, 1);
                      const end = new Date(newYear, endMonth, 0);
                      if (start > end) {
                        alert('종료 기간이 시작 기간보다 이전일 수 없습니다. 다시 선택해주세요.');
                        return;
                      }
                      handlePeriodChange(startYear, startMonth, newYear, endMonth);
                    }}
                    className="period-select"
                  >
                    {Array.from({ length: 201 }, (_, i) => {
                      const currentYear = new Date().getFullYear();
                      const year = currentYear - 100 + i;
                      return (
                        <option key={year} value={year}>{year}년</option>
                      );
                    })}
                  </select>
                  <select 
                    value={endMonth}
                    onChange={(e) => {
                      const newMonth = Number(e.target.value);
                      // 종료기간이 시작기간보다 과거인지 검증
                      const startDate = new Date(startYear, startMonth - 1, 1);
                      const endDate = new Date(endYear, newMonth, 0);
                      if (startDate > endDate) {
                        alert('종료 기간이 시작 기간보다 이전일 수 없습니다. 다시 선택해주세요.');
                        return;
                      }
                      // 모든 값을 명시적으로 전달 (클로저 문제 방지)
                      handlePeriodChange(startYear, startMonth, endYear, newMonth);
                    }}
                    className="period-select"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <option key={month} value={month}>{month}월</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {periodSummaries.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
              <p>선택한 기간에 데이터가 없습니다.</p>
            </div>
          ) : (
            <>
              <div className="period-info">
                총 {periodSummaries.length}개월의 데이터가 표시됩니다
              </div>

              {/* PC: 테이블 */}
              <div className="monthly-table-container">
                <table className="monthly-table">
                  <thead>
                    <tr>
                      <th>월</th>
                      <th>수입</th>
                      <th>지출</th>
                      <th>저축</th>
                      <th>잔액</th>
                      <th>누적 잔액</th>
                      <th>순수 현금</th>
                      <th>투자시드 증액</th>
                      <th>실제 금액</th>
                      <th>차이</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periodSummaries.map(summary => {
                      const seedAddition = summary.investmentSeedAddition ?? 0;
                      const hasActual = summary.actualBalance !== undefined;
                      const netCashDisplay = hasActual
                        ? summary.cashBalance
                        : summary.cashBalance - seedAddition;
                      const hasSeed = summary.investmentSeedAddition != null && summary.investmentSeedAddition !== 0;
                      const differenceDisplay = summary.difference !== undefined
                        ? summary.difference + seedAddition
                        : undefined;
                      return (
                      <tr key={`${summary.year}-${summary.month}`}>
                        <td>{summary.year}년 {summary.month}월</td>
                        <td className="income">{summary.totalIncome.toLocaleString()}</td>
                        <td className="expense">{summary.totalExpense.toLocaleString()}</td>
                        <td className="savings">{summary.savings.toLocaleString()}</td>
                        <td className={summary.balance >= 0 ? 'positive' : 'negative'}>
                          {summary.balance.toLocaleString()}
                        </td>
                        <td className={summary.accumulatedBalance >= 0 ? 'positive' : 'negative'}>
                          {summary.accumulatedBalance.toLocaleString()}
                        </td>
                        <td className={netCashDisplay >= 0 ? 'positive' : 'negative'}>
                          {netCashDisplay.toLocaleString()}
                        </td>
                        <td>
                          <div
                            className="monthly-value-box"
                            onDoubleClick={() => {
                              setEditingMonthSeed({ year: summary.year, month: summary.month });
                              setMonthSeedInput(summary.investmentSeedAddition?.toString() || '');
                            }}
                            title="더블클릭하여 입력 또는 수정"
                          >
                            {hasSeed ? summary.investmentSeedAddition!.toLocaleString() : '-'}
                          </div>
                        </td>
                        <td>
                          <div
                            className="monthly-value-box"
                            onDoubleClick={() => {
                              setEditingMonthBalance({ year: summary.year, month: summary.month });
                              setMonthBalanceInput(summary.actualBalance?.toString() || '');
                            }}
                            title="더블클릭하여 입력 또는 수정"
                          >
                            {summary.actualBalance !== undefined ? summary.actualBalance.toLocaleString() : '-'}
                          </div>
                        </td>
                        <td className={summary.difference !== undefined && differenceDisplay !== undefined && differenceDisplay !== 0
                          ? (differenceDisplay >= 0 ? 'positive' : 'negative')
                          : ''}>
                          {differenceDisplay !== undefined
                            ? (differenceDisplay >= 0 ? '+' : '') + differenceDisplay.toLocaleString()
                            : '-'}
                        </td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>

              {/* 값 입력/수정용 내부 모달 */}
              {(editingMonthBalance || editingMonthSeed) && (() => {
                const isActual = editingMonthBalance !== null;
                const year = isActual ? editingMonthBalance!.year : editingMonthSeed!.year;
                const month = isActual ? editingMonthBalance!.month : editingMonthSeed!.month;
                const title = isActual
                  ? `${year}년 ${month}월 실제 금액`
                  : `${year}년 ${month}월 투자시드 증액`;
                const value = isActual ? monthBalanceInput : monthSeedInput;
                const setValue = isActual ? setMonthBalanceInput : setMonthSeedInput;
                const onSave = () => {
                  if (isActual) saveMonthlyBalance(year, month);
                  else saveMonthlySeedAddition(year, month);
                  if (isActual) { setEditingMonthBalance(null); setMonthBalanceInput(''); }
                  else { setEditingMonthSeed(null); setMonthSeedInput(''); }
                };
                const onCancel = () => {
                  if (isActual) { setEditingMonthBalance(null); setMonthBalanceInput(''); }
                  else { setEditingMonthSeed(null); setMonthSeedInput(''); }
                };
                const onDelete = () => {
                  if (isActual) {
                    if (window.confirm('실제 금액을 삭제하시겠습니까?')) {
                      deleteMonthlyBalance(year, month);
                      onCancel();
                    }
                  } else {
                    if (window.confirm('투자시드 증액을 삭제하시겠습니까?')) {
                      saveMonthlySeedAddition(year, month, 0);
                      onCancel();
                    }
                  }
                };
                const hasExistingValue = isActual
                  ? monthlySummaries.some(s => s.year === year && s.month === month && s.actualBalance !== undefined)
                  : monthlySummaries.some(s => s.year === year && s.month === month && (s.investmentSeedAddition ?? 0) !== 0);
                return (
                  <div className="monthly-edit-modal-overlay" onClick={(e) => e.target === e.currentTarget && onCancel()}>
                    <div className="monthly-edit-modal" onClick={e => e.stopPropagation()}>
                      <h3 className="monthly-edit-modal-title">{title}</h3>
                      <input
                        type="number"
                        className="actual-balance-input monthly-edit-modal-input"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder={isActual ? '실제 금액' : '투자시드 증액'}
                        autoFocus
                      />
                      <div className="balance-actions monthly-edit-modal-actions">
                        {hasExistingValue ? (
                          <>
                            <button onClick={onDelete} className="delete-balance-btn">삭제</button>
                            <button onClick={onSave} className="save-balance-btn">저장</button>
                            <button onClick={onCancel} className="cancel-balance-btn">취소</button>
                          </>
                        ) : (
                          <>
                            <button onClick={onSave} className="save-balance-btn">저장</button>
                            <button onClick={onCancel} className="cancel-balance-btn">취소</button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* 모바일: 카드 리스트 */}
              <div className="monthly-cards-mobile">
                {periodSummaries.map(summary => {
                  const hasSeed = summary.investmentSeedAddition != null && summary.investmentSeedAddition !== 0;
                  return (
                    <div className="monthly-card" key={`card-${summary.year}-${summary.month}`}>
                      <div className="monthly-card-header">
                        <span className="monthly-card-title">{summary.year}년 {summary.month}월</span>
                      </div>

                      <div className="monthly-card-stats">
                        <div className="card-stat income">
                          <span className="card-stat-label">수입</span>
                          <span className="card-stat-value">{summary.totalIncome.toLocaleString()}</span>
                        </div>
                        <div className="card-stat expense">
                          <span className="card-stat-label">지출</span>
                          <span className="card-stat-value">{summary.totalExpense.toLocaleString()}</span>
                        </div>
                        <div className="card-stat savings">
                          <span className="card-stat-label">저축</span>
                          <span className="card-stat-value">{summary.savings.toLocaleString()}</span>
                        </div>
                      </div>

                      <div className="monthly-card-balances">
                        <div className="card-balance-row">
                          <span className="card-balance-label">잔액</span>
                          <span className={`card-balance-value ${summary.balance >= 0 ? 'positive' : 'negative'}`}>
                            {summary.balance.toLocaleString()}
                          </span>
                        </div>
                        <div className="card-balance-row">
                          <span className="card-balance-label">누적 잔액</span>
                          <span className={`card-balance-value ${summary.accumulatedBalance >= 0 ? 'positive' : 'negative'}`}>
                            {summary.accumulatedBalance.toLocaleString()}
                          </span>
                        </div>
                        <div className="card-balance-row">
                          <span className="card-balance-label">순수 현금</span>
                          <span className={`card-balance-value ${(summary.actualBalance !== undefined ? summary.cashBalance : (summary.cashBalance - (summary.investmentSeedAddition ?? 0))) >= 0 ? 'positive' : 'negative'}`}>
                            {(summary.actualBalance !== undefined ? summary.cashBalance : (summary.cashBalance - (summary.investmentSeedAddition ?? 0))).toLocaleString()}
                          </span>
                        </div>
                        <div className="card-balance-row">
                          <span className="card-balance-label">투자시드 증액</span>
                          <div
                            className="monthly-value-box card-value-box"
                            onDoubleClick={() => {
                              setEditingMonthSeed({ year: summary.year, month: summary.month });
                              setMonthSeedInput(summary.investmentSeedAddition?.toString() || '');
                            }}
                            title="더블클릭하여 입력 또는 수정"
                          >
                            {hasSeed ? summary.investmentSeedAddition!.toLocaleString() : '-'}
                          </div>
                        </div>
                      </div>

                      <div className="monthly-card-actual-section">
                        <div className="card-balance-row">
                          <span className="card-balance-label">실제 금액</span>
                          <div
                            className="monthly-value-box card-value-box"
                            onDoubleClick={() => {
                              setEditingMonthBalance({ year: summary.year, month: summary.month });
                              setMonthBalanceInput(summary.actualBalance?.toString() || '');
                            }}
                            title="더블클릭하여 입력 또는 수정"
                          >
                            {summary.actualBalance !== undefined ? summary.actualBalance.toLocaleString() : '-'}
                          </div>
                        </div>
                        {summary.actualBalance !== undefined && summary.difference !== undefined && (
                          <div className="card-balance-row">
                            <span className="card-balance-label">차이</span>
                            <span className={`card-balance-value ${(summary.difference + (summary.investmentSeedAddition ?? 0)) >= 0 ? 'positive' : 'negative'}`}>
                              {((summary.difference + (summary.investmentSeedAddition ?? 0)) >= 0 ? '+' : '') + (summary.difference + (summary.investmentSeedAddition ?? 0)).toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonthlyAssetModal;

